import React from 'react';
import { X, Star, ShoppingBag, ShoppingCart, EyeOff } from 'lucide-react';

export interface ProductListPreviewData {
  name: string;
  categoryName: string;
  price: number;                 // precio final que ve el cliente
  originalPrice: number | null;  // precio tachado (si hay descuento)
  caption: string | null;        // leyenda de promos por cantidad
  badge: string;
  promoLabel: string;
  promoName: string;
  imageUrl: string | null;
  isActive: boolean;
  isFeatured: boolean;
}

const fmt = (n: number) => n.toLocaleString('es-AR', { maximumFractionDigits: 0 });

// Réplica de ProductCard de la tienda (apps/frontend/src/components/ProductCard.tsx)
const StoreCard: React.FC<{ data: ProductListPreviewData; wide?: boolean }> = ({ data, wide }) => {
  const name = data.name.trim() || 'Nombre del producto';
  const category = data.categoryName || 'Categoría';
  const discountPercent = data.originalPrice && data.originalPrice > data.price
    ? Math.round(((data.originalPrice - data.price) / data.originalPrice) * 100)
    : null;

  return (
    <div className={`bg-white rounded-xl border overflow-hidden flex flex-col ${data.isFeatured ? 'border-amber-300' : 'border-slate-200'}`}>
      <div className="relative aspect-square bg-slate-50 overflow-hidden">
        {data.imageUrl
          ? <img src={data.imageUrl} alt={name} className="w-full h-full object-cover" />
          : <div className="w-full h-full flex items-center justify-center text-slate-300 text-xs">Sin imagen</div>}

        {data.promoLabel ? (
          <span className="absolute top-2 left-2 bg-gradient-to-r from-emerald-600 to-teal-500 text-white text-[9px] font-extrabold px-2 py-0.5 rounded-md shadow-sm uppercase tracking-wider">
            {data.promoLabel}
          </span>
        ) : data.badge ? (
          <span className="absolute top-2 left-2 bg-blue-600 text-white text-[9px] font-bold px-2 py-0.5 rounded-md shadow-sm">
            {data.badge}
          </span>
        ) : null}
        {discountPercent && (
          <span className="absolute top-2 right-2 bg-rose-500 text-white text-[9px] font-bold px-2 py-0.5 rounded-md shadow-sm">
            -{discountPercent}%
          </span>
        )}
        {data.isFeatured && (
          <span className="absolute bottom-2 right-2 flex items-center gap-0.5 bg-amber-400 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-md shadow-sm">
            <Star className="w-2.5 h-2.5 fill-current" />
            Destacado
          </span>
        )}
      </div>

      <div className="p-2.5 flex flex-col gap-1 flex-1">
        <p className="text-[11px] text-slate-400 uppercase tracking-wider font-semibold leading-none">{category}</p>
        <h4 className={`${wide ? 'text-[13px]' : 'text-[12px]'} text-slate-800 font-medium leading-snug line-clamp-2`}>{name}</h4>
        <div className="mt-auto pt-2">
          <p className={`${wide ? 'text-base' : 'text-[15px]'} font-bold text-slate-900 leading-none`}>${fmt(data.price)}</p>
          {data.originalPrice && data.originalPrice > data.price ? (
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="text-[10px] text-slate-400 line-through">${fmt(data.originalPrice)}</span>
              {discountPercent && <span className="text-[10px] text-emerald-600 font-bold">{discountPercent}% OFF</span>}
            </div>
          ) : data.caption ? (
            <p className="text-[10px] text-emerald-600 font-bold mt-0.5 leading-tight">{data.caption}</p>
          ) : data.promoName ? (
            <p className="text-[10px] text-emerald-600 font-bold mt-0.5 truncate">{data.promoName}</p>
          ) : null}
        </div>
      </div>

      <div className="px-2.5 pb-2.5 flex gap-1.5">
        <span className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-[11px] font-semibold border bg-white border-slate-200 text-slate-700">
          <ShoppingCart className="w-3 h-3" /> Agregar
        </span>
        <span className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-[11px] font-bold border bg-blue-600 border-blue-600 text-white">
          <ShoppingBag className="w-3 h-3" /> Comprar
        </span>
      </div>
    </div>
  );
};

export const ProductListPreview: React.FC<{ data: ProductListPreviewData; onClose: () => void }> = ({ data, onClose }) => (
  <div className="fixed inset-0 z-[9999] flex justify-center items-center p-4">
    <div className="fixed inset-0 bg-ink/40 backdrop-blur-sm animate-fade-in" onClick={onClose} />
    <div className="relative w-full max-w-xl max-h-full overflow-y-auto bg-white rounded-2xl shadow-2xl border border-page-border animate-fade-scale">
      <div className="p-5 border-b border-page-border flex justify-between items-center sticky top-0 bg-white z-10">
        <div>
          <h3 className="font-heading font-bold text-lg text-ink">Vista previa en la tienda</h3>
          <p className="text-xs text-ink-faint mt-0.5">Así se verá en el catálogo, con lo cargado hasta ahora.</p>
        </div>
        <button type="button" onClick={onClose} aria-label="Cerrar" className="text-ink-faint hover:text-ink p-1.5 hover:bg-page-surface rounded-lg transition-colors">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="p-5 bg-slate-50 space-y-4">
        {!data.isActive && (
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-700 text-xs rounded-xl px-3 py-2">
            <EyeOff className="w-3.5 h-3.5 flex-shrink-0" />
            El producto está inactivo: no se mostrará en la tienda.
          </div>
        )}
        <div className="flex gap-5 items-start justify-center flex-wrap">
          <div className="w-[170px]">
            <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Celular</span>
            <StoreCard data={data} />
          </div>
          <div className="w-[240px]">
            <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Escritorio</span>
            <StoreCard data={data} wide />
          </div>
        </div>
      </div>
    </div>
  </div>
);
